import { getPromptScreenId } from "@src/features/libraryItem/course/hooks/useCourseScreensAtom";
import RichTextPreview from "@src/features/messaging/RichTextPreview";
import Text from "@src/lib/ui/Text";
import useUser from "@src/features/app/hooks/useUser";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import {
  AvatarProfileImageFragment,
  NotificationFragment,
} from "@src/fragments.generated";
import { Ff, NotificationType } from "@src/types.generated";
import { Route } from "nextjs-routes";
import { useMemo } from "react";

type Return = {
  title: React.ReactElement | string | undefined;
  body: React.ReactElement | string | undefined;
  url: Route | undefined;
  creator:
    | {
        id: number | undefined;
        name: string;
        profileImage: AvatarProfileImageFragment | null | undefined;
      }
    | undefined;
};
export const useNotificationBody = (
  notification: NotificationFragment,
): Return | null => {
  const { user } = useUser();
  const guestFeedbackRecommendationsEnabled = useFeatureFlag(
    Ff.GuestFeedbackRecommendations,
  );
  const draftCourseVersionId =
    notification?.course?.draftVersion.id.toString() || "";
  const draftModuleVersionId =
    notification?.module?.draftVersion.id.toString() || "";
  return useMemo(() => {
    let body;
    let url: Route = {
      pathname: "/library",
      query: { type: "modules" },
    };
    let title;
    let creator;
    if (notification.type === NotificationType.CommentMention) {
      title = (
        <Text
          variant="compact-description"
          multiline
          style={{ marginRight: 2 }}
        >
          <span style={{ fontWeight: 600 }}>
            {notification.comment?.createdBy.name}
          </span>
          {" mentioned you in a comment on "}
          <span style={{ fontWeight: 600 }}>
            {notification.course?.libraryItem.name.en}
          </span>
        </Text>
      );
      body = (
        <RichTextPreview
          text={notification.comment?.text || ""}
          isSelected={false}
        />
      );
      const promptUuid = notification.comment?.thread.prompt?.uuid;
      if (!promptUuid) {
        return null;
      }
      url = {
        pathname: "/builder/course-version/[id]",
        query: {
          id: draftCourseVersionId,
          screenId: promptUuid ? getPromptScreenId(promptUuid) : undefined,
        },
      };
      creator = {
        id: notification.comment?.createdBy.id,
        name: notification.comment?.createdBy.name || "",
        profileImage: notification.comment?.createdBy.profileImage,
      };
    } else if (notification.type === NotificationType.CommentReply) {
      title = (
        <Text
          variant="compact-description"
          multiline
          style={{ marginRight: 2 }}
        >
          <span style={{ fontWeight: 600 }}>
            {notification.comment?.createdBy.name}
          </span>
          {" replied to a comment in "}
          <span style={{ fontWeight: 600 }}>
            {notification.course?.libraryItem.name.en}
          </span>
        </Text>
      );
      body = (
        <RichTextPreview
          text={notification.comment?.text || ""}
          isSelected={false}
        />
      );
      if (notification.course && notification.comment?.thread.prompt) {
        const promptUuid = notification.comment.thread.prompt.uuid;
        url = {
          pathname: "/builder/course-version/[id]",
          query: {
            id: draftCourseVersionId,
            screenId: promptUuid ? getPromptScreenId(promptUuid) : undefined,
          },
        };
      }
      creator = {
        id: notification.comment?.createdBy.id,
        name: notification.comment?.createdBy.name || "",
        profileImage: notification.comment?.createdBy.profileImage,
      };
    } else if (notification.type === NotificationType.NewComment) {
      title = (
        <Text
          variant="compact-description"
          multiline
          style={{ marginRight: 2 }}
        >
          <span style={{ fontWeight: 600 }}>
            {notification.comment?.createdBy.name}
          </span>
          {" commented on "}
          <span style={{ fontWeight: 600 }}>
            {notification.course?.libraryItem.name.en}
          </span>
        </Text>
      );
      body = (
        <RichTextPreview
          text={notification.comment?.text || ""}
          isSelected={false}
        />
      );
      if (notification.course) {
        const promptUuid = notification.comment?.thread.prompt?.uuid;
        url = {
          pathname: "/builder/course-version/[id]",
          query: {
            id: draftCourseVersionId,
            screenId: promptUuid ? getPromptScreenId(promptUuid) : undefined,
          },
        };
      }
      creator = {
        id: notification.comment?.createdBy.id,
        name: notification.comment?.createdBy.name || "",
        profileImage: notification.comment?.createdBy.profileImage,
      };
    } else if (notification.type === NotificationType.NewEditPermission) {
      creator = {
        id: user?.id,
        name: user?.name || "",
        profileImage: user?.profileImage,
      };
      if (notification.course) {
        title = (
          <Text
            variant="compact-description"
            multiline
            style={{ marginRight: 2 }}
          >
            {"You can now edit course "}
            <span style={{ fontWeight: 600 }}>
              {notification.course.libraryItem.name.en}
            </span>
          </Text>
        );
        url = {
          pathname: "/builder/course-version/[id]",
          query: {
            id: draftCourseVersionId,
          },
        };
      } else if (notification.module) {
        title = (
          <Text
            variant="compact-description"
            multiline
            style={{ marginRight: 2 }}
          >
            {"You can now edit module "}
            <span style={{ fontWeight: 600 }}>
              {notification.module.libraryItem.name.en}
            </span>
          </Text>
        );
        url = {
          pathname: "/builder/module-version/[id]",
          query: {
            id: draftModuleVersionId,
          },
        };
      }
    } else if (notification.type === NotificationType.NewCommentPermission) {
      creator = {
        id: user?.id,
        name: user?.name || "",
        profileImage: user?.profileImage,
      };
      if (notification.course) {
        title = (
          <Text
            variant="compact-description"
            multiline
            style={{ marginRight: 2 }}
          >
            {"You can now comment on course "}
            <span style={{ fontWeight: 600 }}>
              {notification.course.libraryItem.name.en}
            </span>
          </Text>
        );
        url = {
          pathname: "/builder/course-version/[id]",
          query: {
            id: draftCourseVersionId,
          },
        };
      }
    } else if (notification.type === NotificationType.NewViewPermission) {
      creator = {
        id: user?.id,
        name: user?.name || "",
        profileImage: user?.profileImage,
      };
      if (notification.module) {
        title = (
          <Text
            variant="compact-description"
            multiline
            style={{ marginRight: 2 }}
          >
            {"You can now view a module "}
            <span style={{ fontWeight: 600 }}>
              {notification.module.libraryItem.name.en}
            </span>
          </Text>
        );
        url = {
          pathname: "/builder/module-version/[id]",
          query: {
            id: draftModuleVersionId,
          },
        };
      }
    } else if (notification.type === NotificationType.SharpReassignment) {
      title = (
        <Text
          variant="compact-description"
          multiline
          style={{ marginRight: 2 }}
        >
          {notification.message}
        </Text>
      );
      url = {
        pathname: "/sexual-harassment-prevention",
      };
    } else if (notification.type === NotificationType.LowCreditBalance) {
      title = (
        <Text
          variant="compact-description"
          multiline
          style={{ marginRight: 2 }}
        >
          {notification.message}
        </Text>
      );
      url = {
        pathname: "/marketplace",
      };
    } else if (
      notification.type ===
      NotificationType.GuestFeedbackRecommendationsAvailable
    ) {
      if (!guestFeedbackRecommendationsEnabled) {
        return null;
      }
      title = (
        <Text
          variant="compact-description"
          multiline
          style={{ marginRight: 2 }}
        >
          {notification.message}
        </Text>
      );
      url = {
        pathname: "/recommendations",
      };
    } else {
      return null;
    }
    return {
      creator,
      title,
      body,
      url,
    };
  }, [
    draftCourseVersionId,
    draftModuleVersionId,
    guestFeedbackRecommendationsEnabled,
    notification.comment?.createdBy.id,
    notification.comment?.createdBy.name,
    notification.comment?.createdBy.profileImage,
    notification.comment?.text,
    notification.comment?.thread.prompt,
    notification.course,
    notification.module,
    notification.message,
    notification.type,
    user?.id,
    user?.name,
    user?.profileImage,
  ]);
};
